import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, Search, Hash, Boxes, Loader2, Upload, FileText, Check, X as XIcon } from "lucide-react";

/**
 * RenewalPanel — Lets the sales rep declare an existing FortiGate parc to renew.
 *
 * Two input modes:
 *   - "model"  : picker over the renewable models list
 *   - "serial" : type a Fortinet serial number, the backend resolves it to a
 *                model and suggests matching renewal license SKUs.
 *
 * Props:
 *   value: array of {model_name, qty, serial?, suggested_renewal_sku?}
 *   onChange: (newArray) => void
 */
export default function RenewalPanel({ value, onChange }) {
  const [groups, setGroups] = useState([]); // [{family, models: [{model_name, hw_sku, family, msrp}]}]
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [inputMode, setInputMode] = useState("model"); // "model" | "serial"
  const [serial, setSerial] = useState("");
  const [serialResult, setSerialResult] = useState(null);
  const [serialLoading, setSerialLoading] = useState(false);
  // Batch CSV upload state
  const [batchResults, setBatchResults] = useState(null); // {total, valid, invalid, results}
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchFileName, setBatchFileName] = useState("");
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get("/renewals/models")
      .then((r) => setGroups(r.data?.groups || []))
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, []);

  const allModels = useMemo(() => groups.flatMap((g) => g.models), [groups]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allModels.slice(0, 60);
    const tokens = q.split(/\s+/);
    return allModels
      .filter((m) => {
        const h = (m.model_name + " " + m.hw_sku + " " + m.family).toLowerCase();
        return tokens.every((t) => h.includes(t));
      })
      .slice(0, 60);
  }, [allModels, search]);

  const updateRow = (idx, patch) => {
    const next = [...value];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const removeRow = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  const addModel = (m, extras = {}) => {
    const exists = value.find((r) => r.model_name === m.model_name);
    if (exists) {
      onChange(value.map((r) =>
        r.model_name === m.model_name ? { ...r, qty: Number(r.qty) + 1, ...extras } : r
      ));
    } else {
      onChange([...value, { model_name: m.model_name, qty: 1, ...extras }]);
    }
    setPickerOpen(false);
    setSearch("");
  };

  const lookupSerial = async () => {
    const s = serial.trim();
    if (!s) return;
    setSerialLoading(true);
    setSerialResult(null);
    try {
      const r = await api.post("/renewals/lookup-serial", { serial: s });
      setSerialResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la résolution");
    } finally {
      setSerialLoading(false);
    }
  };

  const addFromSerial = () => {
    if (!serialResult || !serialResult.valid || !serialResult.model_name) return;
    const reco = (serialResult.suggested_renewal_skus || [])[0];
    addModel(
      { model_name: serialResult.model_name, hw_sku: serialResult.hw_sku },
      {
        serial: serialResult.input,
        suggested_renewal_sku: reco?.sku,
        suggested_renewal_name: reco?.name,
      },
    );
    setSerial("");
    setSerialResult(null);
    toast.success(`${serialResult.model_name} ajouté${reco ? ` · suggestion ${reco.sku}` : ""}`);
  };

  // Parse CSV/TSV/plain text file: keep first column of each non-empty line.
  const parseSerialsFromText = (text) => {
    return text
      .split(/\r?\n/)
      .map((line) => line.split(/[,;\t]/)[0].trim())
      .filter((s) => s.length >= 4);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 1024 * 512) {
      toast.error("Fichier trop volumineux (limite 512 Ko, ~10 000 SN).");
      return;
    }
    setBatchFileName(file.name);
    try {
      const text = await file.text();
      const serials = parseSerialsFromText(text);
      if (serials.length === 0) {
        toast.error("Aucun numéro de série détecté dans le fichier.");
        return;
      }
      if (serials.length > 500) {
        toast.warning(`${serials.length} lignes détectées — seules les 500 premières seront analysées.`);
      }
      setBatchLoading(true);
      setBatchResults(null);
      const r = await api.post("/renewals/lookup-serial-batch", { serials: serials.slice(0, 500) });
      setBatchResults(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la lecture du fichier.");
    } finally {
      setBatchLoading(false);
    }
  };

  const addAllFromBatch = () => {
    if (!batchResults?.results) return;
    let added = 0;
    let next = [...value];
    for (const r of batchResults.results) {
      if (r.duplicate) continue;
      if (!r.valid || !r.model_name) continue;
      const reco = (r.suggested_renewal_skus || [])[0];
      const existing = next.find((x) => x.model_name === r.model_name);
      if (existing) {
        next = next.map((x) => x.model_name === r.model_name ? { ...x, qty: Number(x.qty) + 1 } : x);
      } else {
        next.push({
          model_name: r.model_name,
          qty: 1,
          serial: r.input,
          suggested_renewal_sku: reco?.sku,
          suggested_renewal_name: reco?.name,
        });
      }
      added++;
    }
    onChange(next);
    setBatchResults(null);
    setBatchFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.success(`${added} équipement(s) ajouté(s) au parc.`);
  };

  const clearBatch = () => {
    setBatchResults(null);
    setBatchFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const total = value.reduce((s, r) => s + Number(r.qty || 0), 0);

  return (
    <div
      className="border border-amber-300 bg-amber-50/40 rounded-sm p-5"
      data-testid="renewal-panel"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-amber-700" />
          <div>
            <h3 className="text-sm font-semibold tracking-[0.12em] uppercase text-amber-900">
              Parc à renouveler
            </h3>
            <p className="text-xs text-zinc-600 mt-0.5">
              Listez les FortiGate déjà déployés chez le client. Le BOM contiendra
              uniquement les SKU de renouvellement de licence FortiGuard correspondants.
            </p>
          </div>
        </div>
      </div>

      {/* Input mode toggle */}
      <div className="mb-4 flex items-center gap-3 flex-wrap" data-testid="renewal-input-mode-toggle">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Saisie :</span>
        <div className="inline-flex items-stretch rounded-sm border border-zinc-300 overflow-hidden bg-white" role="group">
          <button
            type="button"
            onClick={() => { setInputMode("model"); setPickerOpen(true); setSerialResult(null); }}
            aria-pressed={inputMode === "model"}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors ${
              inputMode === "model"
                ? "bg-amber-700 text-white"
                : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid="renewal-mode-model"
          >
            <Boxes className="inline h-3 w-3 mr-1.5" /> Par modèle
          </button>
          <button
            type="button"
            onClick={() => { setInputMode("serial"); setPickerOpen(false); }}
            aria-pressed={inputMode === "serial"}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors border-l border-zinc-300 ${
              inputMode === "serial"
                ? "bg-amber-700 text-white"
                : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid="renewal-mode-serial"
          >
            <Hash className="inline h-3 w-3 mr-1.5" /> Par numéro de série
          </button>
        </div>
        {inputMode === "model" && (
          <Button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            size="sm"
            className="bg-amber-700 hover:bg-amber-800 rounded-sm ml-auto"
            data-testid="renewal-add-button"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> {pickerOpen ? "Masquer la liste" : "Ajouter un modèle"}
          </Button>
        )}
      </div>

      {/* Serial input */}
      {inputMode === "serial" && (
        <div className="mb-4 border border-zinc-200 bg-white rounded-sm p-4" data-testid="renewal-serial-input">
          <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-600">
            Numéro de série Fortinet
          </Label>
          <div className="mt-2 flex flex-col sm:flex-row gap-2">
            <Input
              value={serial}
              onChange={(e) => { setSerial(e.target.value); setSerialResult(null); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupSerial(); } }}
              placeholder="Ex: FGT60FTK19012345 · FG6H1FT0000000001 · FG4H1FT0000000001"
              className="rounded-sm h-10 font-mono uppercase tracking-wider"
              data-testid="renewal-serial-field"
            />
            <Button
              type="button"
              onClick={lookupSerial}
              disabled={!serial.trim() || serialLoading}
              className="bg-amber-700 hover:bg-amber-800 rounded-sm h-10 sm:w-40"
              data-testid="renewal-serial-lookup"
            >
              {serialLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Identifier"}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-2">
            Préfixes reconnus : <span className="font-mono">FGT, FG, FWF, FW, FGVM, FG3H1, FG4H1, FG5H1, FG6H1, FG1K, FG2K, FG3K, FG7K</span> — le numéro
            est analysé localement (regex), aucune donnée n'est transmise hors plateforme.
          </p>

          {/* Lookup result card */}
          {serialResult && (
            <div
              className={`mt-3 rounded-sm border p-3 text-sm ${
                serialResult.valid && serialResult.model_name
                  ? "border-green-300 bg-green-50"
                  : "border-red-300 bg-red-50"
              }`}
              data-testid="renewal-serial-result"
            >
              {serialResult.valid && serialResult.model_name ? (
                <>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-green-800">
                        Modèle identifié
                      </p>
                      <p className="text-base font-semibold text-zinc-900 mt-0.5">
                        {serialResult.model_name}
                      </p>
                      <p className="text-xs font-mono text-zinc-600 mt-0.5">
                        SKU HW : {serialResult.hw_sku} · Famille {serialResult.family}
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={addFromSerial}
                      size="sm"
                      className="bg-green-700 hover:bg-green-800 rounded-sm"
                      data-testid="renewal-serial-add"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Ajouter au parc
                    </Button>
                  </div>
                  {serialResult.suggested_renewal_skus?.length > 0 && (
                    <div className="mt-3 border-t border-green-200 pt-3">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-green-800 mb-2">
                        Licences de renouvellement suggérées
                      </p>
                      <div className="space-y-1">
                        {serialResult.suggested_renewal_skus.slice(0, 4).map((s) => (
                          <div key={s.sku} className="flex items-center justify-between text-xs">
                            <span className="font-mono text-zinc-800">
                              <span className="inline-block min-w-[50px] px-1.5 py-0.5 rounded-sm bg-zinc-900 text-white text-[9px] mr-2">
                                {s.tier} · {s.duration_months}M
                              </span>
                              {s.sku}
                            </span>
                            <span className="font-mono text-zinc-700">${(s.price || 0).toLocaleString("fr-FR")}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-red-800">
                  {serialResult.error || "Numéro de série non reconnu."}
                </p>
              )}
            </div>
          )}

          {/* Batch CSV upload */}
          <div className="mt-4 pt-4 border-t border-dashed border-zinc-300" data-testid="renewal-batch-upload-block">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <Label className="text-xs font-semibold tracking-wide uppercase text-zinc-600 flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" />
                  Import en masse (CSV / TXT)
                </Label>
                <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
                  Fichier avec un numéro de série par ligne (ou première colonne CSV/TSV).
                  Jusqu'à 500 SN par upload — les en-têtes et doublons sont ignorés
                  automatiquement.
                </p>
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt,.tsv,text/csv,text/plain"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="renewal-batch-file-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="rounded-sm border-amber-400 text-amber-800 hover:bg-amber-50"
                  data-testid="renewal-batch-upload-button"
                  disabled={batchLoading}
                >
                  {batchLoading ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Choisir un fichier
                </Button>
              </div>
            </div>

            {batchFileName && (
              <p className="mt-2 text-[11px] font-mono text-zinc-600">
                Fichier : {batchFileName}
              </p>
            )}

            {batchResults && (
              <div className="mt-3 border border-zinc-200 bg-white rounded-sm" data-testid="renewal-batch-results">
                <div className="flex items-center justify-between gap-2 flex-wrap px-3 py-2 border-b border-zinc-100 bg-zinc-50">
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="font-mono text-zinc-700">
                      {batchResults.total} ligne{batchResults.total > 1 ? "s" : ""}
                    </span>
                    <span className="px-1.5 py-0.5 rounded-sm bg-green-100 text-green-800 border border-green-200 font-mono">
                      <Check className="inline h-2.5 w-2.5 mr-0.5" />
                      {batchResults.valid} reconnu{batchResults.valid > 1 ? "s" : ""}
                    </span>
                    {batchResults.invalid > 0 && (
                      <span className="px-1.5 py-0.5 rounded-sm bg-red-100 text-red-800 border border-red-200 font-mono">
                        <XIcon className="inline h-2.5 w-2.5 mr-0.5" />
                        {batchResults.invalid} non reconnu{batchResults.invalid > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={clearBatch}
                      className="rounded-sm h-7 text-[11px] text-zinc-500"
                      data-testid="renewal-batch-clear"
                    >
                      Effacer
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={addAllFromBatch}
                      disabled={batchResults.valid === 0}
                      className="rounded-sm h-7 text-[11px] bg-green-700 hover:bg-green-800 disabled:bg-zinc-300"
                      data-testid="renewal-batch-add-all"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Tout ajouter au parc
                    </Button>
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-xs" data-testid="renewal-batch-table">
                    <thead className="bg-white border-b border-zinc-100 text-zinc-500">
                      <tr>
                        <th className="px-3 py-1.5 text-left font-semibold uppercase tracking-wide text-[10px]">N° de série</th>
                        <th className="px-3 py-1.5 text-left font-semibold uppercase tracking-wide text-[10px]">Modèle identifié</th>
                        <th className="px-3 py-1.5 text-left font-semibold uppercase tracking-wide text-[10px]">SKU renouvellement suggéré</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {batchResults.results.map((r, idx) => {
                        const ok = r.valid && r.model_name;
                        const reco = (r.suggested_renewal_skus || [])[0];
                        return (
                          <tr
                            key={`${r.input}-${idx}`}
                            className={ok ? "" : "bg-red-50"}
                            data-testid={`renewal-batch-row-${idx}`}
                          >
                            <td className="px-3 py-1.5 font-mono text-zinc-800">
                              {r.input}
                              {r.duplicate && (
                                <span className="ml-1.5 text-[9px] uppercase tracking-wide px-1 py-0.5 rounded-sm bg-zinc-200 text-zinc-600">
                                  doublon
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5">
                              {ok ? (
                                <span className="text-zinc-900 font-medium">{r.model_name}</span>
                              ) : (
                                <span className="text-red-700 text-[11px]">
                                  {r.error || "Non reconnu"}
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 font-mono text-zinc-700">
                              {ok && reco ? (
                                <span className="text-[11px]">
                                  <span className="inline-block px-1 py-0.5 rounded-sm bg-zinc-900 text-white text-[9px] mr-1">
                                    {reco.tier}·{reco.duration_months}M
                                  </span>
                                  {reco.sku}
                                </span>
                              ) : (
                                <span className="text-zinc-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {pickerOpen && inputMode === "model" && (
        <div className="mb-4 border border-zinc-200 bg-white rounded-sm p-3" data-testid="renewal-picker">
          <div className="relative mb-2">
            <Search className="h-4 w-4 text-zinc-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher : FG-100F, FortiGate 60F, FortiWiFi..."
              className="pl-8 rounded-sm h-9"
              data-testid="renewal-picker-search"
            />
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-zinc-100">
            {loading && <div className="p-3 text-xs text-zinc-500">Chargement...</div>}
            {!loading && filtered.length === 0 && (
              <div className="p-3 text-xs text-zinc-500">Aucun modèle trouvé.</div>
            )}
            {!loading && filtered.map((m) => (
              <button
                type="button"
                key={m.hw_sku}
                onClick={() => addModel(m)}
                className="w-full flex items-center justify-between px-2 py-2 text-left text-sm hover:bg-amber-50 rounded-sm"
                data-testid={`renewal-pick-${m.hw_sku}`}
              >
                <span>
                  <span className="font-medium text-zinc-900">{m.model_name}</span>
                  <span className="ml-2 font-mono text-xs text-zinc-500">{m.hw_sku}</span>
                </span>
                <span className="text-xs text-zinc-400">{m.family}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {value.length === 0 ? (
        <div className="border-2 border-dashed border-amber-300 rounded-sm p-6 text-center bg-white">
          <p className="text-sm text-zinc-700 font-medium">Aucun équipement déclaré</p>
          <p className="text-xs text-zinc-500 mt-1">
            Cliquez sur « Ajouter un modèle » pour commencer le parc à renouveler.
          </p>
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
          <table className="w-full text-sm" data-testid="renewal-table">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr>
                <th className="px-4 py-2 text-left text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500">Modèle FortiGate</th>
                <th className="px-4 py-2 text-right text-[11px] font-semibold tracking-[0.12em] uppercase text-zinc-500 w-32">Quantité</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {value.map((row, idx) => (
                <tr key={`${row.model_name}-${idx}`} className="border-b border-zinc-100">
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-zinc-900">{row.model_name}</div>
                    {row.serial && (
                      <div className="text-[10px] font-mono text-zinc-500 mt-0.5 flex items-center gap-1.5">
                        <Hash className="h-2.5 w-2.5" />
                        SN : {row.serial}
                        {row.suggested_renewal_sku && (
                          <span
                            className="ml-2 px-1.5 py-0.5 rounded-sm bg-green-100 text-green-800 border border-green-200 text-[9px] font-semibold"
                            title={row.suggested_renewal_name || row.suggested_renewal_sku}
                          >
                            ↻ {row.suggested_renewal_sku}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Input
                      type="number"
                      min={1}
                      value={row.qty}
                      onChange={(e) => updateRow(idx, { qty: Number(e.target.value) || 0 })}
                      className="rounded-sm h-9 text-right ml-auto max-w-[100px]"
                      data-testid={`renewal-qty-${idx}`}
                    />
                  </td>
                  <td className="px-2 text-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRow(idx)}
                      className="h-8 w-8 p-0 text-zinc-400 hover:text-red-600"
                      data-testid={`renewal-remove-${idx}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 bg-zinc-50 border-t border-zinc-200 flex justify-between text-xs">
            <Label className="text-zinc-500">{value.length} modèle(s) distinct(s)</Label>
            <Label className="font-mono text-zinc-700">Total unités : {total}</Label>
          </div>
        </div>
      )}
    </div>
  );
}
